import { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { toast } from "react-toastify";
import Button from "../../componentes/button/button";
import { CAMPOS_COMPENSADA, letraColuna, mapeamentoCompleto } from "./camposCompensada";

const formatarData = (data) => data ? new Date(data + "T00:00:00").toLocaleDateString("pt-BR") : "-";
const formatarMoeda = (valor) => (valor ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

function ImportarCompensadasModal({ visible, onHide, service, onImported }) {
    const fileInputRef = useRef(null);

    const [step, setStep] = useState("arquivo");
    const [file, setFile] = useState(null);
    const [headers, setHeaders] = useState([]);
    const [mapeamento, setMapeamento] = useState({});
    const [previewData, setPreviewData] = useState(null);
    const [ignorarSemFilial, setIgnorarSemFilial] = useState(false);
    const [confirmarSubstituicao, setConfirmarSubstituicao] = useState(false);

    const [loadingHeaders, setLoadingHeaders] = useState(false);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [loadingConfirmar, setLoadingConfirmar] = useState(false);

    useEffect(() => {
        if (!visible) {
            resetar();
        }
    }, [visible]);

    const resetar = () => {
        setStep("arquivo");
        setFile(null);
        setHeaders([]);
        setPreviewData(null);
        setIgnorarSemFilial(false);
        setConfirmarSubstituicao(false);
    };

    const handleFileChange = async (e) => {
        const selected = e.target.files?.[0];
        e.target.value = "";
        if (!selected) return;

        setFile(selected);
        setLoadingHeaders(true);

        try {
            const [resHeaders, resConfig] = await Promise.all([
                service.lerCabecalho(selected),
                service.getColunasConfig(),
            ]);

            setHeaders(resHeaders.data || []);
            setMapeamento(resConfig.data || {});
            setStep("mapeamento");
        } finally {
            setLoadingHeaders(false);
        }
    };

    const handleMapeamentoChange = (campo, valor) => {
        setMapeamento(prev => ({ ...prev, [campo]: valor === "" ? null : Number(valor) }));
    };

    const handlePreview = async () => {
        setLoadingPreview(true);
        try {
            const res = await service.preview(file, mapeamento);
            setPreviewData(res.data);
            setIgnorarSemFilial(false);
            setStep("preview");
        } finally {
            setLoadingPreview(false);
        }
    };

    const iniciarConfirmacao = () => {
        if (previewData.registrosExistentesNoPeriodo > 0) {
            setConfirmarSubstituicao(true);
            return;
        }
        doConfirmar();
    };

    const doConfirmar = async () => {
        setConfirmarSubstituicao(false);
        setLoadingConfirmar(true);
        try {
            const res = await service.confirmar(file, mapeamento, ignorarSemFilial);
            toast.success(`Importação concluída: ${res.data.registrosImportados} registros importados.`);
            onImported?.();
            onHide();
        } finally {
            setLoadingConfirmar(false);
        }
    };

    const podePreVisualizar = mapeamentoCompleto(mapeamento);
    const podeConfirmar = previewData && (previewData.registrosSemFilial === 0 || ignorarSemFilial);

    return (
        <Dialog
            header="Importar Excel — Pagamento de Compensadas"
            visible={visible}
            onHide={onHide}
            blockScroll
            className="w-[95%] md:w-[720px]"
        >
            {step === "arquivo" && (
                <div className="flex flex-col items-center justify-center gap-4 py-10">
                    <p className="text-sm text-muted-foreground text-center max-w-md">
                        Selecione a planilha Excel (.xlsx) com os pagamentos compensados. Sempre será
                        considerada a primeira aba do arquivo.
                    </p>

                    <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept=".xlsx,.xls"
                        onChange={handleFileChange}
                    />

                    <Button
                        label="Selecionar Arquivo"
                        iconLeft="fa fa-upload"
                        variant="indigo"
                        loading={loadingHeaders}
                        onClick={() => fileInputRef.current?.click()}
                    />
                </div>
            )}

            {step === "mapeamento" && (
                <div>
                    <p className="text-sm text-muted-foreground mb-1">Arquivo: <strong>{file?.name}</strong></p>
                    <p className="text-sm text-muted-foreground mb-4">
                        Confirme qual coluna da planilha corresponde a cada campo antes de pré-visualizar.
                    </p>

                    <div className="flex flex-col gap-3 max-h-[50vh] overflow-y-auto pr-1">
                        {CAMPOS_COMPENSADA.map(({ campo, label }) => (
                            <div key={campo} className="flex items-center justify-between gap-3">
                                <label className="text-sm text-foreground">{label}</label>
                                <select
                                    value={mapeamento[campo] ?? ""}
                                    onChange={(e) => handleMapeamentoChange(campo, e.target.value)}
                                    className="border border-border rounded-md px-2 py-1.5 text-sm bg-card text-foreground w-64"
                                >
                                    <option value="">Selecione a coluna...</option>
                                    {headers.map((h, i) => (
                                        <option key={i} value={i}>{letraColuna(i)} — {h || "(sem título)"}</option>
                                    ))}
                                </select>
                            </div>
                        ))}
                    </div>

                    <div className="flex justify-between gap-2 mt-6 pt-3 border-t border-border">
                        <Button label="Voltar" variant="gray" onClick={() => setStep("arquivo")} />
                        <Button
                            label="Pré-visualizar"
                            iconRight="fa fa-arrow-right"
                            variant="indigo"
                            loading={loadingPreview}
                            disabled={!podePreVisualizar}
                            onClick={handlePreview}
                        />
                    </div>
                </div>
            )}

            {step === "preview" && previewData && (
                <div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <Kpi label="Registros encontrados" valor={previewData.registrosEncontrados} />
                        <Kpi label="Fornecedores" valor={previewData.quantidadeFornecedores} />
                        <Kpi label="Filiais identificadas" valor={previewData.quantidadeFiliaisIdentificadas} />
                        <Kpi
                            label="Sem filial"
                            valor={previewData.registrosSemFilial}
                            destaque={previewData.registrosSemFilial > 0}
                        />
                    </div>

                    <p className="text-sm text-foreground mb-1">
                        <strong>Período de compensação:</strong> {formatarData(previewData.dataCompensacaoMin)} até {formatarData(previewData.dataCompensacaoMax)}
                    </p>

                    {previewData.registrosExistentesNoPeriodo > 0 && (
                        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-2">
                            Já existem <strong>{previewData.registrosExistentesNoPeriodo}</strong> registro(s) importado(s)
                            para esse período. Eles serão substituídos ao confirmar.
                        </p>
                    )}

                    {previewData.registrosSemFilial > 0 && (
                        <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2 mt-2">
                            <p className="mb-1">
                                <strong>{previewData.registrosSemFilial}</strong> registro(s) não têm Filial identificada
                                pelo Código Fornecedor:
                            </p>
                            <p className="text-xs mb-2">
                                {previewData.codigosFornecedorSemFilial?.join(", ") || "-"}
                            </p>
                            <label className="flex items-center gap-2 text-foreground text-xs font-medium">
                                <input
                                    type="checkbox"
                                    checked={ignorarSemFilial}
                                    onChange={(e) => setIgnorarSemFilial(e.target.checked)}
                                />
                                Importar mesmo assim, marcando esses registros como Sem Filial
                            </label>
                        </div>
                    )}

                    <div className="mt-4">
                        <p className="text-sm font-semibold text-foreground mb-2">Primeiros registros</p>
                        <div className="overflow-x-auto max-h-[240px] overflow-y-auto border border-border rounded-lg">
                            <table className="w-full text-xs">
                                <thead className="bg-muted sticky top-0">
                                    <tr>
                                        <th className="px-2 py-1.5 text-left">Nº Doc.</th>
                                        <th className="px-2 py-1.5 text-left">Fornecedor</th>
                                        <th className="px-2 py-1.5 text-left">Filial</th>
                                        <th className="px-2 py-1.5 text-right">Valor</th>
                                        <th className="px-2 py-1.5 text-left">Dt. Compensação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewData.primeirosRegistros?.map((r, i) => (
                                        <tr key={i} className="border-t border-border">
                                            <td className="px-2 py-1.5">{r.numeroDocumento}</td>
                                            <td className="px-2 py-1.5">{r.codigoFornecedor}</td>
                                            <td className={`px-2 py-1.5 ${!r.filialId ? "text-destructive" : ""}`}>
                                                {r.filialNome || "Sem filial"}
                                            </td>
                                            <td className="px-2 py-1.5 text-right">{formatarMoeda(r.valor)}</td>
                                            <td className="px-2 py-1.5">{formatarData(r.dataCompensacao)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="flex justify-between gap-2 mt-6 pt-3 border-t border-border">
                        <Button label="Voltar" variant="gray" onClick={() => setStep("mapeamento")} />
                        <Button
                            label="Confirmar Importação"
                            iconLeft="fa fa-check"
                            variant="green"
                            loading={loadingConfirmar}
                            disabled={!podeConfirmar}
                            onClick={iniciarConfirmacao}
                        />
                    </div>
                </div>
            )}

            <Dialog
                header="Substituir registros existentes?"
                visible={confirmarSubstituicao}
                onHide={() => setConfirmarSubstituicao(false)}
                blockScroll
                className="w-[95%] md:w-[420px]"
            >
                <p className="text-sm text-foreground mb-4">
                    O arquivo contém dados de <strong>{formatarData(previewData?.dataCompensacaoMin)}</strong> até{" "}
                    <strong>{formatarData(previewData?.dataCompensacaoMax)}</strong>.
                    Já existem <strong>{previewData?.registrosExistentesNoPeriodo}</strong> registros neste período.
                    Deseja substituir os dados existentes?
                </p>
                <div className="flex justify-end gap-2">
                    <Button label="Cancelar" variant="gray" onClick={() => setConfirmarSubstituicao(false)} />
                    <Button label="Apagar e Importar" variant="red" loading={loadingConfirmar} onClick={doConfirmar} />
                </div>
            </Dialog>
        </Dialog>
    );
}

function Kpi({ label, valor, destaque }) {
    return (
        <div className={`rounded-lg border p-3 ${destaque ? "border-destructive/40 bg-destructive/10" : "border-border bg-muted/40"}`}>
            <p className="text-lg font-bold text-foreground">{valor ?? 0}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
        </div>
    );
}

export default ImportarCompensadasModal;
